import * as React from 'react';
import Pluralize from 'pluralize';

import useScaledTableColumnWidths from '../../../../Shared/hooks/useScaledTableColumnWidths';
import { DataTable, Tooltip, DataTableChip, getExtraSmallIconForActivityClassification } from '../../../../Shared/index';
import { nonRelevantActivityClassificationIds } from '../../../../../modules/activity_classifications';
import { requestGet } from '../../../../../modules/request';
import { assignedClassrooms } from '../../assignment_flow/classroom_assignment';

interface Classroom {
  assignOnJoin?: boolean;
  assignedStudentCount: number;
  name: string;
  totalStudentCount: number;
}

interface ClassroomActivity {
  activityClassificationId: number;
  activityId: number;
  completedCount: number;
  cumulativeScore: number;
  name: string;
}

interface ActivityPackProps {
  data: {
    unitId: number;
    unitName: string;
    classrooms: Classroom[];
    classroomActivities: Map<number, ClassroomActivity>;
  };
}

const ActivityPack: React.FC<ActivityPackProps> = ({ data }) => {
  const containerRef = React.useRef<HTMLElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    additionalWidthRestrictions: 2,
    columnWidths: [
      { minWidth: 500, width: 620 },
      { minWidth: 150, width: 256 },
      { minWidth: 100, width: 256 },
    ],
    containerRef,
    maxTableWidth: 1126,
    minTableWidth: 750,
  });

  const headers = [
    {
      attribute: 'activity',
      name: 'Activity',
      renderCellsAsHeaders: true,
      width: columnWidths['620w-500min'],
    },
    {
      attribute: 'completion',
      name: 'Completion',
      width: columnWidths['256w-150min'],
    },
    {
      attribute: 'averageScore',
      name: 'Average score',
      rowSectionClassName: 'average-score',
      width: columnWidths['256w-100min'],
    },
  ];

  const { unitName, classroomActivities, unitId, classrooms } = data;

  const classroomsWithAssignment = assignedClassrooms(classrooms);

  const numberOfStudentsAssignedToUnit = () =>
    classroomsWithAssignment.reduce((sum, c) => sum + c.assignedStudentCount, 0);

  const handleReportLinkClick = (unitId: number, activityId: number) => {
    requestGet(
      `/teachers/progress_reports/report_from_unit_and_activity/u/${unitId}/a/${activityId}`,
      (data) => {
        window.location.href = data.url;
      },
      () =>
        alert(
          'This report is not yet available. Please check back when at least one of your students has completed this activity.'
        )
    );
  };

  const renderAssignedToSection = () => (
    <div className="assigned-to">
      <span>
        Assigned to {classroomsWithAssignment.length} {Pluralize('class', classroomsWithAssignment.length)}:
      </span>
      <ul>
        {classroomsWithAssignment.map((c, i) => (
          <li key={i}>
            <b>{c.name}</b>
            <span>
              ({c.assignedStudentCount}/{c.totalStudentCount}{' '}
              {Pluralize('student', c.totalStudentCount)})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderPieChart = (completedCount: number) => {
    const percent = Math.min(completedCount / numberOfStudentsAssignedToUnit(), 1);
    const largeArcFlag = percent > 0.5 ? 1 : 0;
    const angle = 2 * Math.PI * percent;
    const x = Math.cos(angle);
    const y = Math.sin(angle);
    const pathData = `M 1 0 A 1 1 0 ${largeArcFlag} 1 ${x} ${y} L 0 0`;

    return (
      <svg className="activity-analysis-pie-chart" viewBox="-1 -1 2 2">
        <path d={pathData} fill="#348fdf" />
      </svg>
    );
  };

  const calculateAverageScore = (
    cumulativeScore: number,
    completedCount: number,
    activityClassificationId: number
  ) => {
    const averageScore = cumulativeScore / completedCount;

    if (isNaN(averageScore)) {
      return '—';
    } else if (nonRelevantActivityClassificationIds.includes(activityClassificationId)) {
      return (
        <Tooltip tooltipText="This type of activity is not graded." tooltipTriggerText="Not scored" />
      );
    } else if (Math.round(averageScore).toString().length === 2) {
      return `${averageScore.toPrecision(2)}%`;
    }

    return `${averageScore}%`;
  };

  const rows = () =>
    Array.from(classroomActivities.values()).map((ca) => {
      const {
        activityClassificationId,
        name,
        completedCount,
        cumulativeScore,
        activityId,
      } = ca;

      const handleClickForReport = () => handleReportLinkClick(unitId, activityId);
      // If a future contributor adds a clickable element to a cell (a link, a button), CSS will need to be updated — clicks on it would fall through to the overlay → navigate. We can broaden the selector to
      // button: not(.view - report - button), a, .quill - tooltip - trigger if/when that case arises (app/assets/stylesheets/pages/progress_reports/activity_analysis.scss).
      return {
        averageScore: (
          <React.Fragment>
            {calculateAverageScore(cumulativeScore, completedCount, activityClassificationId)}
            <button
              aria-label={`View report for ${name}`}
              className="quill-button extra-small green outlined focus-on-light view-report-button"
              onClick={handleClickForReport}
              type="button"
            >
              View report
            </button>
          </React.Fragment>
        ),
        completion: (
          <React.Fragment>
            {renderPieChart(completedCount)}
            {completedCount} of {numberOfStudentsAssignedToUnit()}{' '}
            {Pluralize('student', numberOfStudentsAssignedToUnit())}
          </React.Fragment>
        ),
        id: activityId,
        activity: name

      };
    });

  if (classroomActivities.size === 0) return null;

  return (
    <section className="activity-analysis-activity-pack" id={`unit-${unitId}`} ref={containerRef}>
      <div className="activity-analysis-activity-pack-header">
        <h2>
          <span>Activity pack:</span>
          <b>{unitName}</b>
        </h2>
        {renderAssignedToSection()}
      </div>
      <DataTable headers={headers} reportingFormat rows={rows()} />
    </section>
  );
};

export default ActivityPack;
